import { VARIETIES_DATA_INDEX } from 'constants/gcConstants';
import {
  GcVariety,
  GSeedSpecies,
  MergedCrop,
  SEED_SPECIES_PRODUCTION_KEYS,
  SeedSpeciesProductionFields,
  SeedSpeciesProductionKey,
} from 'interfaces/IGlobalCatalogue';
import { MExtraVarietyData, MSeedSpecies } from 'interfaces/IMasterDb';

export interface CatalogueGBase {
  globalId: string;
  name: string;
}

export interface CatalogueMBase {
  globalId: string;
  name?: string;
  isExcluded?: boolean;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

// Per-field merge rule: nested objects merge one level deep, an empty array on
// m never blanks out a populated array from the other side, and scalars only
// override when m actually has a value (null/undefined means "not overridden").
const mergeField = (existing: unknown, value: unknown): unknown => {
  if (isPlainObject(existing) && isPlainObject(value)) {
    return { ...existing, ...value };
  }
  if (Array.isArray(existing) && Array.isArray(value)) {
    return value.length ? value : existing;
  }
  return value ?? existing;
};

const mergeInto = <G extends CatalogueGBase, M extends CatalogueMBase>(
  existing: G,
  m: M
): G => {
  const merged: Record<string, unknown> = {
    ...(existing as unknown as Record<string, unknown>),
  };
  (Object.keys(m) as (keyof M)[]).forEach((key) => {
    if (key === 'isExcluded' || key === 'extras') return;
    merged[key as string] = mergeField(
      (existing as Record<string, unknown>)[key as string],
      m[key]
    );
  });
  return merged as unknown as G;
};

// Same regex-escape convention as useMSearchCollection, so client-side MC
// filtering matches the same terms the GC search endpoint would match.
export const filterByNameTerm = <M extends CatalogueMBase>(
  items: M[],
  searchTerm: string
): M[] => {
  if (!searchTerm) return [];
  const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escapedTerm, 'i');
  return items.filter((item) => item.name && regex.test(item.name));
};

/**
 * Generic G+M catalogue merge. Handles excluded items, field-level overrides, and
 * M-only entries for any collection. Pass `applyExtras` for collections with
 * array-level overrides (e.g. varieties on crops).
 */
export const composeList = <G extends CatalogueGBase, M extends CatalogueMBase>(
  gItems: G[],
  mItems: M[],
  applyExtras?: (merged: G, mItem: M) => G
): G[] => {
  const map = new Map<string, G>(gItems.map((g) => [g.globalId, { ...g }]));

  for (const m of mItems) {
    if (m.isExcluded) {
      map.delete(m.globalId);
    } else if (map.has(m.globalId)) {
      const merged = mergeInto(map.get(m.globalId)!, m);
      map.set(m.globalId, applyExtras ? applyExtras(merged, m) : merged);
    } else {
      if (!m.name) continue;
      const base = mergeInto(
        { globalId: m.globalId, name: m.name } as unknown as G,
        m
      );
      map.set(m.globalId, applyExtras ? applyExtras(base, m) : base);
    }
  }

  return Array.from(map.values()).sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '')
  );
};

export const mergeVarieties = (
  gVarieties: GcVariety[],
  mExtraData: MExtraVarietyData[]
): GcVariety[] => {
  if (!mExtraData.length) return gVarieties;

  const map = new Map<string, GcVariety>(
    gVarieties.map((v) => [v.varietyId, { ...v }])
  );

  for (const m of mExtraData) {
    if (m.isExcluded) {
      map.delete(m.varietyId);
    } else if (map.has(m.varietyId)) {
      map.set(m.varietyId, { ...map.get(m.varietyId)!, ...m });
    } else {
      map.set(m.varietyId, {
        varietyId: m.varietyId,
        varietyName: m.varietyName,
        techStackId: m.techStackId,
        snapTechStack: m.snapTechStack,
        manufacturerId: m.manufacturerId,
        snapManufacturer: m.snapManufacturer,
        supplierId: m.supplierId,
        snapSupplier: m.snapSupplier,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) =>
    (a.varietyName ?? '').localeCompare(b.varietyName ?? '', undefined, {
      sensitivity: 'base',
    })
  );
};

/** Harvest/product/residue values from a crop, null where missing (or for every key when there is no crop). */
export const pickSeedSpeciesProductionFields = (
  item: Partial<Pick<GSeedSpecies, SeedSpeciesProductionKey>> | null
): SeedSpeciesProductionFields =>
  Object.fromEntries(
    SEED_SPECIES_PRODUCTION_KEYS.map((key) => [key, item?.[key] ?? null])
  ) as SeedSpeciesProductionFields;

export const composeSeedSpeciesList = (
  gCrops: GSeedSpecies[],
  mCrops: MSeedSpecies[]
): MergedCrop[] => {
  const result = composeList<GSeedSpecies, MSeedSpecies>(
    gCrops,
    mCrops,
    (merged, mCrop) => {
      const varietiesExtra = mCrop.extras?.find(
        (e) => e.attrArrayName === VARIETIES_DATA_INDEX
      );
      return {
        ...merged,
        varieties: varietiesExtra
          ? mergeVarieties(merged.varieties ?? [], varietiesExtra.extraData)
          : (merged.varieties ?? []),
      };
    }
  );

  return result.map((item) => ({
    globalId: item.globalId,
    name: item.name,
    varieties: item.varieties ?? [],
    cost: item.cost ?? null,
    rate: item.rate ?? null,
    rateUnit: item.rateUnit ?? null,
    ...pickSeedSpeciesProductionFields(item),
  }));
};
